import React from 'react';
import { useTranslation } from 'react-i18next';
import { SpeakingSituation } from '../../../types/lesson';
import { useLessonPersistedState } from '../useLessonPersistedState';
import TranslateToggle from './TranslateToggle';

interface Props {
    lessonId?: number;
    items: SpeakingSituation[];
    sourceLanguage?: string;
    targetLanguage?: string;
}

const SpeakingChallengeSection: React.FC<Props> = ({ lessonId, items, sourceLanguage, targetLanguage }) => {
    const { t } = useTranslation('lesson');
    const [shown, setShown] = useLessonPersistedState<Record<number, boolean>>(lessonId, 'speaking.shown', {});
    const [showTranslation, setShowTranslation] = useLessonPersistedState<Record<number, boolean>>(lessonId, 'speaking.showTranslation', {});
    const [fallback, setFallback] = useLessonPersistedState<Record<number, string>>(lessonId, 'speaking.fallbackTranslation', {});

    return (
        <div className="lesson-panel">
            <div>
                <div className="lesson-panel-kicker">{t('sections.speak.kicker', 'Speak')}</div>
                <h2>{t('sections.speak.title', 'Speaking Challenge')}</h2>
                <p className="lesson-panel-desc">
                    {t('sections.speak.desc', "Talk it through with a partner before revealing the useful language.")}
                </p>
            </div>
            <div className="lesson-qgrid">
                {items.map((item, i) => (
                    <div className="lesson-situation-card" key={i}>
                        <h3>{t('sections.speak.situation_n', 'Situation {{n}}', { n: i + 1 })}</h3>
                        {/* div, not p — TranslateToggle's revealed translation renders a block-level
                            div, which is invalid nested directly inside a <p>. */}
                        <div className="lesson-word-target">
                            {item.situation}
                            <TranslateToggle
                                text={item.situation}
                                native={item.situation_native}
                                fallback={fallback[i]}
                                onFallbackFetched={(text) => setFallback((prev) => ({ ...prev, [i]: text }))}
                                sourceLanguage={sourceLanguage}
                                targetLanguage={targetLanguage}
                                shown={!!showTranslation[i]}
                                onToggle={() => setShowTranslation((prev) => ({ ...prev, [i]: !prev[i] }))}
                            />
                        </div>
                        <p style={{ margin: 0, fontSize: 12.5, fontWeight: 700, color: 'var(--color-forest-500, var(--primary-color))' }}>
                            {t('sections.speak.challenge', 'Challenge: use at least 5 expressions from today\'s lesson.')}
                        </p>
                        <button className="lesson-btn ghost" style={{ alignSelf: 'flex-start' }} onClick={() => setShown((prev) => ({ ...prev, [i]: !prev[i] }))}>
                            {t('sections.speak.show_language', 'Show useful language')}
                        </button>
                        {shown[i] && (
                            <div className="lesson-reveal lesson-word-target">
                                <ul>
                                    {item.useful_language.map((l, li) => <li key={li}>{l}</li>)}
                                </ul>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default SpeakingChallengeSection;
